import { Button, cn } from '@my/ui';
import { Check } from 'lucide-react';
import {
  IMPORT_RESOURCE_TYPES,
  REFERENCE_LABEL,
  TYPE_HINT,
  TYPE_LABEL,
  type ImportResourceType,
} from '../lib/types';

interface TypeStepProps {
  type: ImportResourceType | null;
  onTypeChange: (type: ImportResourceType) => void;
  onContinue: () => void;
}

/**
 * Step 1 — pick the resource type for the batch. It drives everything
 * downstream: which fields the mapping step offers, what `reference` means, and
 * whether an equipment type applies. One import = one type; run the wizard
 * again for the next fleet file.
 */
export function TypeStep({ type, onTypeChange, onContinue }: TypeStepProps) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Every row in the file is imported as this resource type. Import one type at a time.
      </p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {IMPORT_RESOURCE_TYPES.map((t) => {
          const active = t === type;
          return (
            <button
              key={t}
              type="button"
              onClick={() => onTypeChange(t)}
              aria-pressed={active}
              className={cn(
                'flex items-start gap-3 rounded-md border p-3 text-left transition-colors',
                active
                  ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                  : 'border-slate-300 bg-white hover:border-slate-400',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  active ? 'border-purple-600 bg-purple-600 text-white' : 'border-slate-300',
                )}
              >
                {active && <Check className="h-3 w-3" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900">{TYPE_LABEL[t]}</span>
                <span className="block text-[11px] text-slate-500">{TYPE_HINT[t]}</span>
                <span className="mt-0.5 block text-[10px] uppercase tracking-wide text-slate-400">
                  Reference = {REFERENCE_LABEL[t]}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex justify-end border-t border-slate-200 pt-3">
        <Button type="button" onClick={onContinue} disabled={!type}>
          Continue
        </Button>
      </div>
    </div>
  );
}
